import { Injectable } from "@angular/core";
import { UserManager, User, WebStorageStateStore, UserManagerSettings } from "oidc-client";
import { BehaviorSubject } from "rxjs";
import { LogService } from "./log.service";
import { EnvironmentConfig } from "../models/environment-config.model";
import { LocalStorageService } from "../services/local-storage.service";
import { Router } from "@angular/router";
import { BrowserUtils } from "../utils/browser-utils";

declare var environment: EnvironmentConfig;

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  /** LocalStorageService key. Value `spa` means this browser finished the website OTP login. */
  static readonly identitySessionStorageKey = "identitySession";
  static readonly identitySessionSpa = "spa";
  /** sessionStorage flag set before the Identity hop. signin-callback.html promotes it to identitySession. */
  static readonly spaLoginPendingKey = "rubbl.spaLoginPending";
  /**
   * Website OTP login replaced the Identity HTML redirect when #407 merged
   * (2026-09-21T23:31:26Z). Implicit tokens issued before that are the old session.
   */
  static readonly spaLoginCutoffSeconds = 1790033486;

  private userManager: UserManager;
  private userManagerConfig: UserManagerSettings;

  private user: User;
  user$ = new BehaviorSubject<User>(null);
  /** Concurrent 401s share one clear and one redirect to /login. */
  private unauthorizedRedirectInFlight = false;

  public initialized = new BehaviorSubject<boolean>(false);
  public onLoggedIn = new BehaviorSubject<any>(null);
  private logoutHandlers: Array<() => void> = [];

  constructor(private logService: LogService, private localStorageService: LocalStorageService, private router: Router) {
    const skipOidc = BrowserUtils.isPrerenderUserAgent();
    // Must run before UserManager reads localStorage. A pre-SPA implicit token stays
    // unexpired for 28 days; APIs reject it and /login bounces a still-signed-in user
    // back onto the failing page, which leaves a blank screen.
    if (!skipOidc) {
      this.dropLegacyIdentitySession();
    }

    this.userManagerConfig = {
      authority: `${environment.identityProvider}/`,
      client_id: environment.clientId,
      redirect_uri: environment.clientLoginRedirectUri,
      automaticSilentRenew: !skipOidc,
      silent_redirect_uri: environment.clientSilentLoginRedirectUri,
      post_logout_redirect_uri: environment.clientLogoutRedirectUri,
      response_type: "id_token token",
      scope: environment.identityScope ? environment.identityScope : "openid catalog inventory account user estimate contracts profile inspections",
      filterProtocolClaims: true,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
    };

    this.userManager = new UserManager(this.userManagerConfig);

    if (skipOidc) {
      this.queryStringLogic();
      return;
    }

    this.userManager
      .getUser()
      .then(user => {
        if (user && !this.accessSessionIsTrusted(user)) {
          this.clearLocalSession();
          return;
        }

        if (user && !user.expired) {
          this.user = user;
          this.user$.next(user);
          if (user.profile) {
            this.logService.setUser(user.profile.sub, user.profile.account_id);
          }
          this.localStorageService.set("hasLoggedIn", true);
          this.honorRedirectPathQuery();
          return;
        }

        // kind of a hack
        if (this.localStorageService.get("hasLoggedIn")) {
          // try to sign in the user silently.
          this.userManager.signinSilent().then(
            user => {
              if (user && !this.accessSessionIsTrusted(user)) {
                this.clearLocalSession();
                return;
              }
              this.user = user;
              this.user$.next(user);
              if (user && user.profile) {
                this.logService.setUser(user.profile.sub, user.profile.account_id);
              }
              this.honorRedirectPathQuery();
            },
            () => {
              this.localStorageService.remove("hasLoggedIn");
              this.discardOidcUser();
              this.logService.clearUser();
            },
          );
        }
      })
      .catch(err => this.swallowAuthRejection(err));
    this.queryStringLogic();
    // Keep this service in sync whenever oidc-client loads or renews a user: the silent-renew
    // callback, the silent-callback.html hand-off, and any session that hydrates *after* the
    // constructor's getUser() has already resolved (e.g. right after an SPA OTP token hop). Without
    // re-running the redirect check here, a leftover same-origin `?redirectPath=` — such as landing
    // on `/renter/dashboard?redirectPath=/connect` after login — would never be honored, and the
    // user$ stream would hold a stale token after a renew.
    this.userManager.events.addUserLoaded(user => this.onUserLoaded(user));
  }

  private onUserLoaded(user: User): void {
    if (user && !this.accessSessionIsTrusted(user)) {
      this.clearLocalSession();
      return;
    }

    this.user = user;
    this.unauthorizedRedirectInFlight = false;

    if (!user || user.expired) {
      return;
    }

    this.user$.next(user);
    if (user.profile) {
      this.logService.setUser(user.profile.sub, user.profile.account_id);
    }
    this.localStorageService.set("hasLoggedIn", true);
    this.honorRedirectPathQuery();
  }

  queryStringLogic() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("login")) {
      this.logService.pushTagToGtm({
        event: "accountAccess",
        accessType: "login",
        client: environment.clientId,
      });
      urlParams.delete("login");
    }

    if (urlParams.get("signup")) {
      this.logService.pushTagToGtm({
        event: "accountAccess",
        accessType: "signUp",
        client: environment.clientId,
      });
      urlParams.delete("signup");
    }

    // we need to store any first visit query params such as utm paramters so we can send it to the signup
    if (!this.localStorageService.get("firstVisitQueryParams")) {
      let firstVisitQueryParams = {};
      for (var value of urlParams["keys"]()) {
        firstVisitQueryParams[value] = urlParams.get(value);
      }

      if (Object.keys(firstVisitQueryParams).length != 0) {
        this.localStorageService.set("firstVisitQueryParams", firstVisitQueryParams);
      }
    }
  }

  getUser(): Promise<User> {
    return this.userManager
      .getUser()
      .then(user => {
        if (user && !this.accessSessionIsTrusted(user)) {
          this.clearLocalSession();
          return null;
        }
        return user;
      })
      .catch(err => {
        this.swallowAuthRejection(err);
        return null;
      });
  }

  getUserNonAsync() {
    return this.user;
  }

  login(redirectPath: string = "/equipment-rentals"): Promise<void> {
    if (BrowserUtils.isPrerenderUserAgent()) {
      return Promise.resolve();
    }

    redirectPath = this.prepareAuthRedirectPath(redirectPath, "login");

    if (environment.clientId === "rubbl.website") {
      Promise.resolve(this.router.navigate(["/login"], { queryParams: { redirectPath } })).catch(err => this.swallowAuthRejection(err));
      return Promise.resolve();
    }

    return this.userManager.signinRedirect({ state: { redirectPath } });
  }

  completeSpaLogin(ticket: string, redirectPath: string = "/equipment-rentals", eventName: "login" | "signup" = "login"): Promise<void> {
    const spaRedirectPath = this.safeAppPath(redirectPath);
    redirectPath = this.prepareAuthRedirectPath(spaRedirectPath, eventName);
    this.persistSpaRedirectPath(redirectPath);
    this.markSpaLoginPending();

    return this.userManager.createSigninRequest({ state: { redirectPath } }).then(req => {
      this.submitCompleteSpaLoginForm(ticket, req.url, spaRedirectPath);
    });
  }

  loginWithAzureAd(redirectPath: string = "/equipment-rentals"): Promise<void> {
    redirectPath = this.prepareAuthRedirectPath(redirectPath, "login");

    return this.userManager.createSigninRequest({ state: { redirectPath } }).then(req => {
      const keepAuth = this.userManagerConfig.authority.replace("/auth/", "");
      const localUrl = req.url.replace(keepAuth, "");
      const challengeUrl = `${this.userManagerConfig.authority}External/Challenge?provider=rubbl-azure-ad&returnUrl=${encodeURIComponent(localUrl)}`;
      this.navigateAway(challengeUrl);
    });
  }

  registerLogoutHandler(handler: () => void): void {
    if (handler) {
      this.logoutHandlers.push(handler);
    }
  }

  logout(): Promise<void> {
    this.clearLocalSession();
    return this.userManager.signoutRedirect();
  }

  /**
   * The API rejected the bearer token. Clear it before sending the user to /login.
   * Leaving the session in place makes the login page treat them as signed in and
   * navigate straight back to the page that just failed.
   */
  invalidateRejectedSession(): void {
    this.handleUnauthorizedApiResponse(false);
  }

  /**
   * One clear + one /login navigation for a burst of 401s.
   * `requestHadBearer` covers calls that already sent the rejected token, including
   * admin-api /lead/session, even if in-memory state was already dropped.
   */
  handleUnauthorizedApiResponse(requestHadBearer = false): void {
    const hadSession = requestHadBearer || this.user != null || !!this.readStoredOidcUser();
    if (!hadSession) {
      return;
    }

    if (this.unauthorizedRedirectInFlight) {
      this.clearLocalSession();
      return;
    }

    this.unauthorizedRedirectInFlight = true;
    this.clearLocalSession();

    const onAuthRoute = typeof window !== "undefined" && this.isAuthRoute(window.location.pathname || "");
    if (!onAuthRoute) {
      const returnPath = this.currentReturnPath();
      Promise.resolve(this.login(returnPath)).catch(err => this.swallowAuthRejection(err));
    }

    // Release after this turn so parallel 401 callbacks share one redirect,
    // while a later session can still be signed out.
    Promise.resolve().then(() => {
      this.unauthorizedRedirectInFlight = false;
    });
  }

  isLoggedIn(): boolean {
    return !!this.user && !this.user.expired && this.accessSessionIsTrusted(this.user);
  }

  getAccessToken(): string {
    if (!this.isLoggedIn()) {
      return "";
    }

    return this.user.access_token || "";
  }

  getRoles(): string[] {
    if (!this.user) {
      return null;
    }

    return Array.isArray(this.user.profile.role) ? this.user.profile.role : [this.user.profile.role];
  }

  signInSilent() {
    if (BrowserUtils.isPrerenderUserAgent()) {
      return Promise.resolve(null);
    }

    return this.userManager.signinSilent();
  }

  signoutRedirectCallback(): Promise<any> {
    return this.userManager.signoutRedirectCallback();
  }

  signUp(redirectPath: string = "/equipment-rentals"): Promise<any> {
    if (BrowserUtils.isPrerenderUserAgent()) {
      return Promise.resolve();
    }

    if (this.user) {
      // user is already logged in
      this.router.navigate([redirectPath]);
      return;
    }

    redirectPath = this.prepareAuthRedirectPath(redirectPath, "signup");

    if (environment.clientId === "rubbl.website") {
      this.router.navigate(["/signup"], { queryParams: this.signupQueryParams(redirectPath) });
      return Promise.resolve();
    }

    const firstVisitQueryParams = this.localStorageService.get("firstVisitQueryParams");
    let firstVisitQueryParamsString = "";

    if (firstVisitQueryParams) {
      const urlParams = new URLSearchParams(window.location.search);
      // if we have an affId query string but the user has already been to the site and the affId is not in local storage lets grab the aff id from the query string
      // its an edge case, but someone couldn have came from facebook. then saw an affiliates post and used the affiliates signup link
      if (urlParams.get("affId") != null && !firstVisitQueryParams.hasOwnProperty("affId")) {
        firstVisitQueryParams.affId = urlParams.get("affId");
      }

      firstVisitQueryParamsString = Object.keys(firstVisitQueryParams)
        .map(key => key + "=" + firstVisitQueryParams[key])
        .join("&");
    }

    return this.userManager.createSigninRequest({ state: { redirectPath } }).then(req => {
      const keepAuth = this.userManagerConfig.authority.replace("/auth/", "");
      const localUrl = req.url.replace(keepAuth, "");
      window.location.href = `${this.userManagerConfig.authority}Account/SignUp?${firstVisitQueryParamsString}&returnUrl=${encodeURIComponent(localUrl)}`;
    });
  }

  setUser(user) {
    this.user = user;
    this.onLoggedIn.next(user);
  }

  /**
   * After a live session exists, treat leftover `?redirectPath=` as the destination
   * (same-origin relative paths only). Fixes landings like `/renter/dashboard?redirectPath=/connect`.
   */
  honorRedirectPathQuery(): void {
    if (typeof window === "undefined" || !this.user || this.user.expired) {
      return;
    }

    const raw = new URLSearchParams(window.location.search).get("redirectPath");
    if (!this.isSafeAppPath(raw)) {
      return;
    }

    const currentPath = window.location.pathname || "";
    if (this.isAuthRoute(currentPath)) {
      return;
    }

    const target = raw.trim();
    const targetPathname = target.split("?")[0];
    if (targetPathname === currentPath) {
      return;
    }

    this.markHonorExactRedirect(target);
    Promise.resolve(this.router.navigateByUrl(target)).catch(err => this.swallowAuthRejection(err));
  }

  private signupQueryParams(redirectPath: string): Record<string, string> {
    const queryParams: Record<string, string> = { redirectPath };
    const current = new URLSearchParams(window.location.search);
    ["affId", "capability", "accountId"].forEach(key => {
      const value = current.get(key);
      if (value) {
        queryParams[key] = value;
      }
    });
    return queryParams;
  }

  private prepareAuthRedirectPath(redirectPath: string, eventName: "login" | "signup"): string {
    redirectPath = this.safeAppPath(redirectPath);

    if (environment.routePrefix && !redirectPath.startsWith(environment.routePrefix)) {
      redirectPath = `${environment.routePrefix}${redirectPath}`; // the redirect path comes with a trailing slash
    }

    const flag = `${eventName}=true`;
    if (!redirectPath.includes(flag)) {
      redirectPath += redirectPath.includes("?") ? `&${flag}` : `?${flag}`;
    }
    return redirectPath;
  }

  static readonly spaRedirectStorageKey = "rubbl.spaRedirectPath";
  static readonly honorExactRedirectStorageKey = "rubbl.spaHonorExactRedirect";

  /**
   * Remember that the next `/connect` load came from an explicit redirectPath
   * of exactly `/connect` (not `/connect/hauling`). Connect layout must not
   * replace that destination with the first enabled tab.
   */
  markHonorExactRedirect(redirectPath: string): void {
    if (typeof window === "undefined" || !window.sessionStorage || !this.isSafeAppPath(redirectPath)) {
      return;
    }

    if (!this.isConnectRootPath(redirectPath)) {
      return;
    }

    try {
      window.sessionStorage.setItem(AuthenticationService.honorExactRedirectStorageKey, "1");
    } catch {
      // Private mode / quota — Connect may still auto-tab.
    }
  }

  static consumeHonorExactRedirect(): boolean {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return false;
    }

    try {
      const raw = window.sessionStorage.getItem(AuthenticationService.honorExactRedirectStorageKey);
      if (raw) {
        window.sessionStorage.removeItem(AuthenticationService.honorExactRedirectStorageKey);
        return true;
      }
    } catch {
      return false;
    }

    return false;
  }

  private isConnectRootPath(redirectPath: string): boolean {
    const pathname = redirectPath.trim().split("?")[0].replace(/\/+$/, "") || "/";
    return pathname === "/connect";
  }

  private safeAppPath(redirectPath: string): string {
    const fallback = "/equipment-rentals";
    return this.isSafeAppPath(redirectPath) ? redirectPath.trim() : fallback;
  }

  private isSafeAppPath(redirectPath: string): boolean {
    if (!redirectPath || typeof redirectPath !== "string") {
      return false;
    }

    const path = redirectPath.trim();
    return path.startsWith("/") && !path.startsWith("//") && !path.includes("://") && !path.includes("\\");
  }

  private isAuthRoute(path: string): boolean {
    return path === "/login" || path === "/signup" || path.endsWith("/login") || path.endsWith("/signup");
  }

  /**
   * Website sessions from the Identity HTML login are still unexpired for up to 28 days.
   * Remove that oidc user and the hasLoggedIn flag so silent renew cannot mint another
   * token from the old Identity cookie. Sessions issued after the SPA login shipped are
   * left alone until an API actually rejects them.
   */
  private dropLegacyIdentitySession(): void {
    if (environment.clientId !== "rubbl.website") {
      return;
    }

    if (!this.storedWebsiteAccessTokenIsUntrusted()) {
      return;
    }

    this.removeStoredOidcUser();
    this.localStorageService.remove("hasLoggedIn");
    this.localStorageService.remove(AuthenticationService.identitySessionStorageKey);
    try {
      window.sessionStorage.removeItem(AuthenticationService.spaLoginPendingKey);
    } catch {
      // sessionStorage can throw in private mode.
    }
  }

  /**
   * A future expires_at is not a session. The access token itself must be a JWT
   * issued on or after the SPA OTP cutoff. id_token / profile iat cannot stand in
   * for a missing or unreadable access-token iat, and the SPA flag cannot either.
   */
  private storedWebsiteAccessTokenIsUntrusted(): boolean {
    const raw = this.readStoredOidcUser();
    if (!raw) {
      return false;
    }

    try {
      const stored = JSON.parse(raw);
      return !this.accessTokenIsTrusted(stored?.access_token);
    } catch {
      return true;
    }
  }

  private accessSessionIsTrusted(user: User): boolean {
    if (!user) {
      return false;
    }

    if (environment.clientId !== "rubbl.website") {
      return true;
    }

    return this.accessTokenIsTrusted(user.access_token);
  }

  private accessTokenIsTrusted(token: string): boolean {
    const issuedAt = this.jwtIssuedAt(token);
    return issuedAt != null && issuedAt >= AuthenticationService.spaLoginCutoffSeconds;
  }

  private jwtIssuedAt(token: string): number | null {
    if (!token || typeof token !== "string") {
      return null;
    }

    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }

    try {
      const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      const padded = payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const claims = JSON.parse(atob(padded));
      return typeof claims.iat === "number" ? claims.iat : null;
    } catch {
      return null;
    }
  }

  private readStoredOidcUser(): string | null {
    const key = this.storedOidcUserKey();
    return key ? window.localStorage.getItem(key) : null;
  }

  private markSpaLoginPending(): void {
    if (environment.clientId !== "rubbl.website" || typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    try {
      window.sessionStorage.setItem(AuthenticationService.spaLoginPendingKey, "1");
    } catch {
      // Private mode — signin-callback will not be able to mark the session.
    }
  }

  private clearLocalSession(): void {
    this.logoutHandlers.forEach(handler => {
      try {
        handler();
      } catch {
        // Website Connect cleanup must not block Identity sign-out.
      }
    });
    this.user = null;
    this.user$.next(null);
    this.localStorageService.remove("hasLoggedIn");
    this.localStorageService.remove(AuthenticationService.identitySessionStorageKey);
    this.removeStoredOidcUser();
    this.discardOidcUser();
    this.logService.clearUser();
  }

  private discardOidcUser(): void {
    Promise.resolve(this.userManager.removeUser()).catch(err => this.swallowAuthRejection(err));
    Promise.resolve(this.userManager.clearStaleState()).catch(err => this.swallowAuthRejection(err));
  }

  /**
   * Zone reports `Promise.reject(false)` as "Uncaught (in promise): [object Boolean]".
   * oidc-client and the tag manager both reject with a boolean when a silent hop or
   * script load fails. Swallow that so it cannot blank the console or the page.
   */
  private swallowAuthRejection(err: unknown): void {
    if (typeof err === "boolean" || err == null) {
      return;
    }
  }

  private currentReturnPath(): string {
    if (typeof window === "undefined") {
      return "/equipment-rentals";
    }

    const path = `${window.location.pathname || ""}${window.location.search || ""}`;
    return this.isSafeAppPath(path) ? path : "/equipment-rentals";
  }

  private removeStoredOidcUser(): void {
    const key = this.storedOidcUserKey();
    if (key) {
      window.localStorage.removeItem(key);
    }
  }

  private storedOidcUserKey(): string | null {
    if (typeof window === "undefined" || !window.localStorage || !environment.clientId) {
      return null;
    }

    const suffix = ":" + environment.clientId;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.indexOf("oidc.user:") === 0 && key.endsWith(suffix)) {
        return key;
      }
    }

    return null;
  }

  private persistSpaRedirectPath(redirectPath: string): void {
    if (typeof window === "undefined" || !window.sessionStorage) {
      return;
    }

    try {
      window.sessionStorage.setItem(AuthenticationService.spaRedirectStorageKey, redirectPath);
    } catch {
      // Private mode / quota — oidc state is the fallback.
    }
    this.markHonorExactRedirect(redirectPath);
  }

  private submitCompleteSpaLoginForm(ticket: string, returnUrl: string, spaRedirectPath: string): void {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = `${this.userManagerConfig.authority}Account/CompleteSpaLogin`;

    const ticketInput = document.createElement("input");
    ticketInput.type = "hidden";
    ticketInput.name = "ticket";
    ticketInput.value = ticket;
    form.appendChild(ticketInput);

    const returnUrlInput = document.createElement("input");
    returnUrlInput.type = "hidden";
    returnUrlInput.name = "returnUrl";
    returnUrlInput.value = returnUrl;
    form.appendChild(returnUrlInput);

    const spaRedirectInput = document.createElement("input");
    spaRedirectInput.type = "hidden";
    spaRedirectInput.name = "spaRedirectPath";
    spaRedirectInput.value = spaRedirectPath;
    form.appendChild(spaRedirectInput);

    document.body.appendChild(form);
    form.submit();
  }

  private navigateAway(url: string): void {
    window.location.href = url;
  }
}
